import React, { useEffect, useState } from 'react';
import { useTheme } from '../ThemeContext';
import { agentFetch } from '../utils/agentApi';
import { getApiUrl } from '../utils/platform';
import { API_KEY_ATTESTATION } from '../shared/attestation';
import { apiV1Url } from '../shared/siteUrl';

const ATTESTATION = API_KEY_ATTESTATION;
const DEFAULT_KEY_NAME = 'Zapier';

const Integrations = ({ profile }) => {
  const { colors } = useTheme();
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState(DEFAULT_KEY_NAME);
  const [attested, setAttested] = useState(false);
  const [freshKey, setFreshKey] = useState('');
  const [copied, setCopied] = useState('');
  const [testKey, setTestKey] = useState('');
  const [testResult, setTestResult] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { ok, body } = await agentFetch('/api/keys');
      if (!ok) {
        setError(body.error || 'Could not load your keys.');
        setKeys([]);
      } else {
        setKeys(body.keys || []);
        setError('');
      }
    } catch (err) {
      setError(err.message || 'Could not load your keys.');
      setKeys([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [profile?.id]);

  const createKey = async (event) => {
    event.preventDefault();
    setError('');
    setCreating(true);
    try {
      const { ok, body } = await agentFetch('/api/keys', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim() || DEFAULT_KEY_NAME,
          attested_relationship: attested,
        }),
      });
      if (!ok) {
        setError(body.error || 'Could not create a key.');
        return;
      }
      setFreshKey(body.key);
      setTestKey(body.key);
      setCopied('');
      setName(DEFAULT_KEY_NAME);
      setAttested(false);
      await load();
    } catch (err) {
      setError(err.message || 'Could not create a key.');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm('Turn off this key? Zapier will stop adding clients with it.')) return;
    try {
      const { ok, body } = await agentFetch('/api/keys', {
        method: 'DELETE',
        body: JSON.stringify({ id }),
      });
      if (!ok) {
        setError(body.error || 'Could not turn off that key.');
        return;
      }
      await load();
    } catch (err) {
      setError(err.message || 'Could not turn off that key.');
    }
  };

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
    } catch (err) {
      setCopied('');
      setError('Could not copy. Select the text and copy it yourself.');
    }
  };

  const testConnection = async () => {
    setTestResult('');
    const response = await fetch(getApiUrl('/api/v1/me'), {
      headers: { Authorization: `Bearer ${testKey.trim()}` },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setTestResult(body.code || 'Connection failed');
      return;
    }
    setTestResult(`Connected as ${body.agent_name} (${body.homeowners_used} / ${body.homeowners_limit} homeowners)`);
  };

  const homeownersUrl = apiV1Url('homeowners', window.location);
  const curl = `curl -X POST ${homeownersUrl} \\
  -H "Authorization: Bearer ${freshKey || 'pk_live_YOUR_KEY'}" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: deal-123" \\
  -d '{"first_name":"Pat","last_name":"Lee","email":"pat.lee@leehome.net","external_id":"crm-contact-1","send_invite":false}'`;

  const card = {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '0.75rem',
    padding: '1.25rem',
    marginBottom: '1rem',
  };
  const field = {
    display: 'block',
    width: '100%',
    maxWidth: '520px',
    padding: '0.65rem 0.75rem',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    background: colors.background,
    color: colors.text,
    fontSize: '0.95rem',
  };
  const primaryButton = {
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    border: 'none',
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: '44px',
  };
  const quietButton = {
    marginLeft: '0.5rem',
    padding: '0.55rem 0.9rem',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
    cursor: 'pointer',
    minHeight: '44px',
  };
  const activeKeys = keys.filter((key) => !key.revoked_at);

  return (
    <div>
      <h2 data-tour="integrations" style={{ color: colors.text, marginBottom: '0.35rem' }}>Integrations</h2>
      <p style={{ color: colors.textSecondary, marginTop: 0 }}>
        Send new clients from Zapier into Pacarion. They are saved here and are not emailed unless you choose that later.
      </p>
      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}

      <div style={card}>
        <h3 style={{ color: colors.text, marginTop: 0 }}>Connect Zapier</h3>
        <p style={{ color: colors.textSecondary, marginTop: 0 }}>
          Create a key, then paste the address and the key into Zapier. You only see the full key once.
        </p>
        <form onSubmit={createKey}>
          <label style={{ display: 'block', margin: '0.75rem 0 1rem', color: colors.text, lineHeight: 1.5 }}>
            <input
              type="checkbox"
              checked={attested}
              onChange={(event) => setAttested(event.target.checked)}
              style={{ marginRight: '0.5rem' }}
            />
            {ATTESTATION}
          </label>
          <button type="submit" style={primaryButton} disabled={!attested || creating}>
            {creating ? 'Creating…' : 'Create key'}
          </button>
        </form>
        {freshKey && (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ color: '#92400e', marginTop: 0 }}>Copy this key now. It will not be shown again.</p>
            <input readOnly value={freshKey} aria-label="New Zapier key" style={field} />
            <button type="button" style={{ ...quietButton, marginLeft: 0, marginTop: '0.5rem' }} onClick={() => copyText(freshKey, 'key')}>
              {copied === 'key' ? 'Copied' : 'Copy key'}
            </button>
          </div>
        )}

        <ol style={{ color: colors.text, lineHeight: 1.6, paddingLeft: '1.2rem', marginBottom: 0 }}>
          <li>In Zapier, choose <strong>Webhooks by Zapier</strong>, then <strong>POST</strong>.</li>
          <li>
            Paste this address:
            <div style={{ marginTop: '0.4rem' }}>
              <input readOnly value={homeownersUrl} aria-label="Zapier address" style={field} />
              <button type="button" style={{ ...quietButton, marginLeft: 0, marginTop: '0.5rem' }} onClick={() => copyText(homeownersUrl, 'address')}>
                {copied === 'address' ? 'Copied' : 'Copy address'}
              </button>
            </div>
          </li>
          <li>Paste your key where Zapier asks for it.</li>
          <li>Match the person’s first name, last name, and email, then turn the Zap on.</li>
        </ol>
      </div>

      <div style={card}>
        <h3 style={{ color: colors.text, marginTop: 0 }}>Your keys</h3>
        {loading ? <p style={{ color: colors.textSecondary }}>Loading…</p> : (
          activeKeys.length === 0 ? (
            <p style={{ color: colors.textSecondary, marginBottom: 0 }}>No keys yet. Create one above to connect Zapier.</p>
          ) : (
            <ul style={{ paddingLeft: '1.1rem', marginBottom: 0 }}>
              {activeKeys.map((key) => (
                <li key={key.id} style={{ marginBottom: '0.6rem', color: colors.text }}>
                  <strong>{key.name}</strong>
                  {!key.revoked_at && (
                    <button type="button" style={quietButton} onClick={() => revoke(key.id)}>Turn off</button>
                  )}
                </li>
              ))}
            </ul>
          )
        )}
      </div>

      <details style={card}>
        <summary style={{ color: colors.text, fontWeight: 600, cursor: 'pointer' }}>Advanced (for developers)</summary>
        <div style={{ marginTop: '1rem', color: colors.textSecondary, lineHeight: 1.55 }}>
          <p>
            POST JSON to <code>{homeownersUrl}</code>. Header <code>Authorization</code> is <code>Bearer</code> plus the key.
            Header <code>Content-Type</code> is <code>application/json</code>.
          </p>
          <p>
            Optional header <code>Idempotency-Key</code> (128 characters or fewer). The same key with the same body returns the original response. A different body returns 409.
          </p>
          <p>
            Send <code>external_id</code> from the CRM contact or deal id so retries stay safe. Leave <code>send_invite</code> out, or set it to false. Set it to true only when that person should get the Pacarion email.
          </p>
          <label style={{ display: 'block', marginBottom: '0.75rem', color: colors.text }}>
            Key name
            <input value={name} onChange={(event) => setName(event.target.value)} maxLength={60} style={{ ...field, marginTop: '0.25rem' }} />
          </label>
          <h4 style={{ color: colors.text, marginBottom: '0.35rem' }}>Test connection</h4>
          <p style={{ marginTop: 0 }}>Calls GET /api/v1/me with the key. This is the same check Zapier uses.</p>
          <input value={testKey} onChange={(event) => setTestKey(event.target.value)} placeholder="pk_live_..." aria-label="Key to test" style={field} />
          <button type="button" style={quietButton} onClick={testConnection} disabled={!testKey.trim()}>Test connection</button>
          {testResult && <p>{testResult}</p>}
          <pre style={{ whiteSpace: 'pre-wrap', background: colors.surfaceAlt, padding: '0.75rem', borderRadius: '0.5rem', color: colors.text }}>{curl}</pre>
        </div>
      </details>
    </div>
  );
};

export default Integrations;
